import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { InlineNotice } from '../components/Feedback';
import { renderMarkdown } from '../lib/markdown';
import { LEGAL_DOCUMENTS, legalDocumentBySlug } from '../lib/legal';

/**
 * The current fragment.
 *
 * Links inside the documents are plain fragment anchors, and a fragment click
 * fires `hashchange` — it does not push a router history entry, so the router's
 * location is not a reliable source for this. Reading the fragment directly
 * covers both the deep link from signup and in-page tab clicks.
 */
function useHash(): string {
  const [hash, setHash] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, ''),
  );

  useEffect(() => {
    const update = (): void => setHash(window.location.hash.replace(/^#/, ''));
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);

  return hash;
}

/**
 * The legal documents the signup form asks a user to agree to.
 *
 * The consent links used to point at `/legal`, which no route served, so the one
 * place a user is asked to accept the terms led nowhere. The documents live as
 * Markdown under `docs/legal/`; this page is the route that shows them.
 *
 * They are drafts awaiting counsel review (see `docs/legal/README.md`), so the
 * notice below is not decoration: it is the difference between showing a user
 * the terms and representing unapproved text as binding.
 */
export function LegalPage(): JSX.Element {
  const slug = useHash();
  const active = legalDocumentBySlug(slug) ?? LEGAL_DOCUMENTS[0]!;

  // The document is chosen by the fragment, so the target element only exists
  // after the render above; scroll once it does.
  useEffect(() => {
    if (!slug) return;
    const target = document.getElementById(slug);
    target?.scrollIntoView({ block: 'start' });
  }, [slug, active.slug]);

  const body = useMemo(
    () => renderMarkdown(active.markdown, legalHref),
    [active],
  );

  return (
    <div className="page guide-page">
      <PageHeader
        title="Legal"
        subtitle="Terms of Service, Privacy Policy, Acceptable Use Policy and Data Processing Addendum."
        actions={
          <Link to="/login" className="btn btn--small">
            Back to sign in
          </Link>
        }
      />

      <InlineNotice tone="warn">
        <div>
          <p className="notice__title">Draft — pending legal review</p>
          <p className="notice__message">
            These documents are engineering drafts. They are not reviewed by a lawyer, contain
            unresolved placeholders, and must not be relied on as final terms. See the draft status
            recorded in <code>docs/legal/README.md</code>.
          </p>
        </div>
      </InlineNotice>

      <nav className="guide-tabs" aria-label="Legal documents">
        {LEGAL_DOCUMENTS.map((document) => (
          <a
            key={document.slug}
            href={`#${document.slug}`}
            className={`guide-tab${document.slug === active.slug ? ' is-active' : ''}`}
            aria-current={document.slug === active.slug ? 'true' : undefined}
          >
            {document.label}
          </a>
        ))}
      </nav>

      <article className="panel legal-doc" id={active.slug}>
        {body}
      </article>
    </div>
  );
}

/**
 * Links between the legal documents point at sibling files (`privacy-policy.md`).
 * Rewrite those to this page's anchors so a reader stays inside the dashboard
 * instead of hitting a 404 on a raw path; anything else is left alone.
 */
function legalHref(href: string): string {
  if (href.startsWith('#')) return href;
  const slug = href.replace(/\.md$/, '').replace(/^\.\//, '');
  // The documents cross-reference each other by filename; keep those inside the
  // page. The documentation index is not a legal document, so send it to the
  // dashboard's own docs surface rather than a path this route does not serve.
  if (slug === 'README') return '/docs';
  return legalDocumentBySlug(slug) ? `#${slug}` : href;
}
